import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Area } from '../../core/constants/area';
import { ConversionClient } from './models/ConversionClient';
import { ConversionDetails } from './models/ConversionDetails';
import { ConversionRecodeCompanyResult } from './models/ConversionMigrationResult';
import { ConversionBulkStepProgress, ConversionMigrationProgress } from './models/ConversionMigrationProgress';
import {
	ConversionAtoBFundingResult,
	ConversionAtoBWalletFunding,
	ConversionClientIdentifier,
	ConversionTermsLookup,
	ConversionClientDebtorRelationship,
	ConversionClientDebtorTie,
	ConversionWinFactorClientNote,
	ConversionWinFactorInvoiceNote,
	ConversionWinFactorCustomerNote,
	ConversionCustomerNoteCustomer,
	ConversionCustomerNoteSendResult,
	ConversionWinFactorFundingOption,
	ConversionDataUploadResult,
	ConversionDataUploadJob,
	ConversionWinFactorClientName,
	ConversionDataUploadSummary,
	ConversionDocument,
	ConversionDocumentClient,
	ConversionDocumentCustomer,
	ConversionDocumentSendResult,
} from './models/ConversionDataUpload';

@Injectable()
export class ConversionService {
	constructor(public httpClient: HttpClient) {}

	/**
	 * excluded = true lists the carriers removed from the conversion list instead of the queue.
	 * converted = true lists the carriers that finished converting (the Converted tab).
	 */
	getConversionClientList(excluded?: boolean, converted?: boolean) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/Client/List`;
		const params = new HttpParams()
			.set('excluded', excluded ? 'true' : 'false')
			.set('converted', converted ? 'true' : 'false');
		return this.httpClient.get<ConversionClient[]>(url, { params });
	}

	/** Puts a converted carrier back on the list at Part 1 or Part 2 so it can be run again. */
	reopenConversionClient(clientKeys: number[], part: number) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/Client/Reopen`;
		return this.httpClient.post<number>(url, { clientKeys: clientKeys || [], part: part });
	}

	/** Removes a carrier from the conversion list because it should not be converted. */
	excludeConversionClient(clientKeys: number[]) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/Client/Exclude`;
		return this.httpClient.post<number>(url, { clientKeys: clientKeys || [] });
	}

	/** Puts a removed carrier back on the conversion list. */
	restoreConversionClient(clientKeys: number[]) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/Client/Restore`;
		return this.httpClient.post<number>(url, { clientKeys: clientKeys || [] });
	}

	getConversionDetails(clientKeys: number[]) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/Details`;

		let params: HttpParams = new HttpParams();
		(clientKeys || []).forEach((clientKey) => {
			params = params.append('clientKeys', clientKey.toString());
		});

		return this.httpClient.get<ConversionDetails>(url, { params });
	}

	/**
	 * Starts part 1 (client, debtors, $0 invoice, client notes) or part 2 (after the invoices
	 * were imported into FactorCloud by hand) for one carrier. The server runs it in the
	 * background and answers at once; follow it with getMigrationProgress(runId).
	 */
	startMigration(clientKeys: number[], runId: string, part: number, includeCompleted: boolean, approveLookbackHours?: number) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/Migrate/Start`;
		return this.httpClient.post<{ runId: string }>(url, {
			clientKeys: clientKeys || [],
			runId: runId || null,
			part: part,
			// False leaves out the steps the carrier already succeeded at (since the server started).
			includeCompleted: includeCompleted === true,
			// Step 16: how many hours back to look for Pending invoices to approve (null = 3).
			approveLookbackHours: approveLookbackHours || null,
		});
	}

	/** Returns null (HTTP 204) until the server has registered the run. */
	getMigrationProgress(runId: string) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/Migrate/Progress`;
		const params = new HttpParams().set('runId', runId);
		return this.httpClient.get<ConversionMigrationProgress>(url, { params });
	}

	/**
	 * "The company code X is already registered": changes the FactorCloud client or debtor that has the
	 * code to the code plus 8888, so the carrier can be created with its own code.
	 */
	recodeCompany(companyType: string, companyId: string, currentCode: string) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/CompanyCode/Recode`;
		return this.httpClient.post<ConversionRecodeCompanyResult>(url, {
			companyType: companyType,
			companyId: companyId,
			currentCode: currentCode,
		});
	}

	/**
	 * Bulk step: starts one conversion step (1-11) for every carrier on the conversion list, in the background, one
	 * carrier at a time. Answers at once with the run id (the running one's, when a bulk step is already running).
	 * stopOnFailure stops it after the first carrier that fails. includeCompleted also runs the carriers an earlier run of
	 * the step already succeeded for (left out otherwise).
	 */
	startBulkStep(step: number, stopOnFailure: boolean, includeCompleted: boolean, approveLookbackHours?: number) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/Bulk/Start`;
		let params = new HttpParams()
			.set('step', step.toString())
			.set('stopOnFailure', stopOnFailure ? 'true' : 'false')
			.set('includeCompleted', includeCompleted ? 'true' : 'false');
		if (approveLookbackHours) {
			// Step 16: how many hours back to look for Pending invoices to approve.
			params = params.set('approveLookbackHours', approveLookbackHours.toString());
		}
		return this.httpClient.post<{ runId: string }>(url, {}, { params });
	}

	/** Returns null (HTTP 204) when the run is unknown. */
	getBulkStepProgress(runId: string) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/Bulk/Progress`;
		const params = new HttpParams().set('runId', runId);
		return this.httpClient.get<ConversionBulkStepProgress>(url, { params });
	}

	/** Stops a running bulk step after the carrier it is on. */
	stopBulkStep(runId: string) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/Bulk/Stop`;
		const params = new HttpParams().set('runId', runId);
		return this.httpClient.post(url, {}, { params });
	}

	/** Step 9: downloads an invoice import error file (FactorCloud's failed rows, saved in processed-invoices). */
	downloadInvoiceImportErrorFile(fileName: string) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/InvoiceImport/ErrorFile`;
		const params = new HttpParams().set('fileName', fileName);
		return this.httpClient.get(url, { params: params, responseType: 'blob' });
	}

	/** Row counts for the Data Upload page. */
	getDataUploadSummary() {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/Summary`;
		return this.httpClient.get<ConversionDataUploadSummary>(url);
	}

	/** Uploads the AtoB digital wallet report (CSV) into FactorCloud.AtoBWallet. */
	uploadAtoBWalletReport(file: File) {
		// Runs in the background on the server and is polled, so a long import is never cut off by a request timeout.
		return this.runDataUploadJob('atob', file);
	}

	/** Uploads the WinFactor client identifiers file (CSV) into WinFactor.ClientIdentifiers. */
	uploadClientIdentifiers(file: File) {
		// Runs in the background on the server and is polled, so a long import is never cut off by a request timeout.
		return this.runDataUploadJob('identifiers', file);
	}

	/** Every row of WinFactor.ClientIdentifiers. */
	getClientIdentifiers() {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/ClientIdentifiers/List`;
		return this.httpClient.get<ConversionClientIdentifier[]>(url);
	}

	/** Uploads the transfer terms file (.xlsx, .xls or .csv) into FactorCloud.TermsLookup. */
	uploadTermsLookup(file: File) {
		// Runs in the background on the server and is polled, so a long import is never cut off by a request timeout.
		return this.runDataUploadJob('terms', file);
	}

	/** Every row of FactorCloud.TermsLookup. */
	getTermsLookups() {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/TermsLookup/List`;
		return this.httpClient.get<ConversionTermsLookup[]>(url);
	}

	/** Uploads the WinFactor Funding Options file (.xlsx, .xls or .csv) into secure.WFBD. */
	uploadFundingOptions(file: File) {
		// Runs in the background on the server and is polled, so a long import is never cut off by a request timeout.
		return this.runDataUploadJob('funding', file);
	}

	/** Every row of secure.WFBD; account and routing numbers come masked. */
	getFundingOptions() {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/FundingOptions/List`;
		return this.httpClient.get<ConversionWinFactorFundingOption[]>(url);
	}

	/** Uploads the WinFactor client debtor relationships file (.xlsx, .xls or .csv) into WinFactor.ClientDebtorRelationships. */
	uploadClientDebtorRelationships(file: File) {
		// Runs in the background on the server and is polled, so a long import is never cut off by a request timeout.
		return this.runDataUploadJob('relationships', file);
	}

	/** Up to 500 saved relationships matching the search (client / customer ID or name). */
	getClientDebtorRelationships(search: string) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/ClientDebtorRelationships/List`;
		const params = new HttpParams().set('search', search || '');
		return this.httpClient.get<ConversionClientDebtorRelationship[]>(url, { params });
	}

	/** Uploads the WinFactor "Active Clients with Customer Info" report (.xlsx, .xls or .csv) into WinFactor.ClientDebtorTies. */
	uploadClientDebtorTies(file: File) {
		// Runs in the background on the server and is polled, so a long import is never cut off by a request timeout.
		return this.runDataUploadJob('ties', file);
	}

	/** Up to 500 saved client debtor ties matching the search (client / customer name). */
	getClientDebtorTies(search: string) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/ClientDebtorTies/List`;
		const params = new HttpParams().set('search', search || '');
		return this.httpClient.get<ConversionClientDebtorTie[]>(url, { params });
	}

	/** Uploads the WinFactor client notes file (.csv, .xlsx or .xls) into WinFactor.ClientNotes. */
	uploadClientNotes(file: File) {
		// Runs in the background on the server and is polled, so a long import is never cut off by a request timeout.
		return this.runDataUploadJob('clientnotes', file);
	}

	/** Up to 500 saved client notes matching the search (client ID or name, user, type, note). */
	getClientNotes(search: string) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/ClientNotes/List`;
		const params = new HttpParams().set('search', search || '');
		return this.httpClient.get<ConversionWinFactorClientNote[]>(url, { params });
	}

	/** Uploads the WinFactor invoice notes file (.xlsx, .xls or .csv) into WinFactor.InvoiceNotes. */
	uploadInvoiceNotes(file: File) {
		// Runs in the background on the server and is polled, so a long import is never cut off by a request timeout.
		return this.runDataUploadJob('invoicenotes', file);
	}

	/** Up to 500 saved invoice notes matching the search (client ID or name, invoice, load, WF invoice ID, customer). */
	getInvoiceNotes(search: string) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/InvoiceNotes/List`;
		const params = new HttpParams().set('search', search || '');
		return this.httpClient.get<ConversionWinFactorInvoiceNote[]>(url, { params });
	}

	/** Uploads the WinFactor customer notes file (.csv, .xlsx or .xls) into WinFactor.CustomerNotes. */
	uploadCustomerNotes(file: File) {
		// Runs in the background on the server and is polled, so a long import is never cut off by a request timeout.
		return this.runDataUploadJob('customernotes', file);
	}

	/** Up to 500 saved customer notes matching the search (customer ID or name, user, type, note). */
	getCustomerNotes(search: string) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/CustomerNotes/List`;
		const params = new HttpParams().set('search', search || '');
		return this.httpClient.get<ConversionWinFactorCustomerNote[]>(url, { params });
	}

	/** Every customer with saved notes (matching the search), with its FactorCloud debtor and how many notes were sent. */
	getCustomerNoteCustomers(search: string) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/CustomerNotes/Customers`;
		const params = new HttpParams().set('search', search || '');
		return this.httpClient.get<ConversionCustomerNoteCustomer[]>(url, { params });
	}

	/** Sends customers' notes to their FactorCloud debtors as General Notes (at most 25 per request). */
	sendCustomerNotes(customerIds: number[]) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/CustomerNotes/Send`;
		return this.httpClient.post<ConversionCustomerNoteSendResult[]>(url, { customerIds: customerIds || [] });
	}

	/**
	 * Lists the WinFactor client documents in blob storage into WinFactor.ClientDocuments, or (customers) the customer
	 * documents into WinFactor.CustomerDocuments.
	 */
	importDocuments(customers: boolean) {
		// Runs in the background on the server and is polled, so a long import is never cut off by a request timeout.
		return this.runDataUploadJob(customers ? 'customerdocuments' : 'clientdocuments', null);
	}

	/**
	 * Starts a Data Upload import in the background (POST DataUpload/Jobs/{kind}) and polls it every 2 seconds until it
	 * finishes, then emits its result, or errors with its message ({ error: message }, as a failed request would). Has no
	 * time limit; a few failed polls in a row (the server restarting, a dropped connection) are retried.
	 */
	runDataUploadJob(kind: string, file: File): Observable<ConversionDataUploadResult> {
		return new Observable<ConversionDataUploadResult>((observer) => {
			let stopped = false;
			let timer: any = null;
			const startUrl = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/Jobs/${kind}`;
			const progressUrl = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/Jobs/Progress`;
			const formData = new FormData();
			if (file) {
				formData.append('file', file, file.name);
			}

			const poll = (runId: string, errors: number) => {
				if (stopped) {
					return;
				}
				const params = new HttpParams().set('runId', runId);
				this.httpClient.get<ConversionDataUploadJob>(progressUrl, { params }).subscribe(
					(job) => {
						if (stopped) {
							return;
						}
						// Not known on the server (it restarted, or the run expired): give up after a few tries.
						if (!job) {
							if (errors >= 10) {
								observer.error({ error: 'The import is no longer running on the server. Check the saved rows, then upload the file again if needed.' });
								return;
							}
							timer = setTimeout(() => poll(runId, errors + 1), 3000);
							return;
						}
						if (job.isComplete) {
							if (job.successful) {
								observer.next(job.result);
								observer.complete();
							} else {
								observer.error({ error: job.error || 'The import failed.' });
							}
							return;
						}
						timer = setTimeout(() => poll(runId, 0), 2000);
					},
					(error) => {
						if (stopped) {
							return;
						}
						if (errors >= 10) {
							observer.error(error);
							return;
						}
						timer = setTimeout(() => poll(runId, errors + 1), 5000);
					}
				);
			};

			this.httpClient.post<{ runId: string }>(startUrl, formData).subscribe(
				(started) => poll(started.runId, 0),
				(error) => observer.error(error)
			);

			return () => {
				stopped = true;
				if (timer) {
					clearTimeout(timer);
				}
			};
		});
	}

	/**
	 * The clients with documents in WinFactor.ClientDocuments, or (customers) the customers with documents in
	 * WinFactor.CustomerDocuments in the same shape: clientId = CustomerID, factorCloudClientId = FactorCloud debtor id.
	 */
	getDocumentClients(customers: boolean): Observable<ConversionDocumentClient[]> {
		if (!customers) {
			const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/ClientDocuments/Clients`;
			return this.httpClient.get<ConversionDocumentClient[]>(url);
		}

		const customerUrl = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/CustomerDocuments/Customers`;
		return this.httpClient.get<ConversionDocumentCustomer[]>(customerUrl).pipe(
			map((rows) =>
				(rows || []).map((row) => {
					const client = new ConversionDocumentClient();
					client.clientId = row.customerId;
					client.clientFolderName = row.customerFolderName;
					client.clientName = row.customerName;
					client.factorCloudClientId = row.factorCloudDebtorId;
					client.documentCount = row.documentCount;
					client.sentCount = row.sentCount;
					return client;
				})
			)
		);
	}

	/** One client's (or customer's) documents; a null id lists the documents outside a client (customer) folder. */
	getDocuments(id: number, customers: boolean) {
		const url = customers
			? `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/CustomerDocuments/List`
			: `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/ClientDocuments/List`;
		let params = new HttpParams();
		if (id !== null && id !== undefined) {
			params = params.set(customers ? 'customerId' : 'clientId', id.toString());
		}
		return this.httpClient.get<ConversionDocument[]>(url, { params });
	}

	/**
	 * Sends documents from blob storage to FactorCloud (POST /documents): client documents to the carrier's FactorCloud
	 * client, customer documents to the FactorCloud debtor. The server picks the type from the file name.
	 */
	sendDocuments(documentKeys: number[], customers: boolean) {
		const url = customers
			? `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/CustomerDocuments/Send`
			: `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/ClientDocuments/Send`;
		return this.httpClient.post<ConversionDocumentSendResult[]>(url, { documentKeys: documentKeys || [] });
	}

	/** The imported AtoB wallets with their FactorCloud client match and funding instruction status. */
	getAtoBWalletFunding() {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/AtoBWallet/Funding`;
		return this.httpClient.get<ConversionAtoBWalletFunding[]>(url);
	}

	/** The active AtoB wallets the conversion would add as funding instructions for one carrier. */
	getAtoBWalletsForClient(winFactorClientId: number, clientName: string) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/AtoBWallet/ForClient`;
		let params = new HttpParams();
		if (winFactorClientId !== null && winFactorClientId !== undefined) {
			params = params.set('winFactorClientId', winFactorClientId.toString());
		}
		if (clientName) {
			params = params.set('clientName', clientName);
		}
		return this.httpClient.get<ConversionAtoBWalletFunding[]>(url, { params });
	}

	/** Active WinFactor clients whose company name matches no AtoB wallet. */
	getWinFactorClientsWithoutAtoBWallet() {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/AtoBWallet/UnmatchedClients`;
		return this.httpClient.get<ConversionWinFactorClientName[]>(url);
	}

	/** Creates the "AtoB Wallet" funding instruction on each wallet's matched FactorCloud client. */
	sendAtoBFundingInstructions(atoBIds: number[]) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/Conversion/DataUpload/AtoBWallet/FundingInstructions`;
		return this.httpClient.post<ConversionAtoBFundingResult[]>(url, { atoBIds: atoBIds || [] });
	}
}



// WEBPACK FOOTER //
// ./src/app/portal/conversion/conversion.service.ts